import React, { useMemo } from 'react'

import { useAppContext } from 'app/useAppContext'
import { useObject } from 'data/hooks/objects'
import {
    filtersToValue,
    formatFilters,
    ObjectFieldsFilterV4 as Filters,
} from 'features/records/components/RecordFilters'
import { ObjectPicker } from 'features/studio/ui/ObjectPicker'
import { DEFAULT_ACTION_ICON } from 'features/workflows/common'
import { FilterValueExpression } from 'features/workflows/common/FilterValueExpression'
import { ItemIcon } from 'features/workflows/ItemIcon'
import { ConfigurationLabel } from 'features/workflows/WorkflowUI'

import { Box } from 'ui/components/Box'
import { Button } from 'ui/components/Button'
import { Container } from 'ui/components/Container'
import { Divider } from 'ui/components/Divider'
import { Select, SelectOption } from 'ui/components/Select'

import { WorkflowActionEditorComponent, WorkflowActionNodeRendererComponent } from './types'

export const CompareRecordsActionNode: WorkflowActionNodeRendererComponent = ({
    action,
    actionType,
    showType,
}) => {
    const { object } = useObject(action.settings?.object_id as string | undefined)

    return (
        <>
            <ItemIcon
                icon={actionType.icon || DEFAULT_ACTION_ICON}
                iconStyle={actionType.icon_style}
                mr="m"
            />

            <Box flex column alignItems="flex-start" fontWeight="bodyRegular">
                {showType && (
                    <Box fontSize="bodyXs" color="textWeakest">
                        Action
                    </Box>
                )}

                <Box flex center>
                    {action.name}
                    {object && (
                        <>
                            {' '}
                            on
                            <Container
                                as="span"
                                ml="s"
                                px="xs"
                                py="2xs"
                                borderRadius="s"
                                variant="accentMuted"
                            >
                                {object?.name}
                            </Container>
                        </>
                    )}
                </Box>
            </Box>
        </>
    )
}

type CompareRecordsActionFilter = FilterValue

export const CompareRecordsActionConfig: WorkflowActionEditorComponent = ({ action, onChange }) => {
    const { selectedStack } = useAppContext()

    const objectId = action.settings?.object_id as string | undefined
    const compareFields = (action.settings?.compare_fields as string[] | undefined) || []
    const identifierField = action.settings?.id_field as string | undefined

    const { object } = useObject(objectId)
    const fields = useMemo(() => {
        if (!object) return []

        return object.fields.filter((f) => !f.connection_options?.is_disabled)
    }, [object])

    const testFiltersValue = useMemo(() => {
        const filters =
            (action.settings?.test_filters as CompareRecordsActionFilter[] | undefined) ?? []

        return filtersToValue(filters, object) as Filter[]
    }, [action.settings?.test_filters, object])

    const referenceFiltersValue = useMemo(() => {
        const filters =
            (action.settings?.reference_filters as CompareRecordsActionFilter[] | undefined) ?? []

        return filtersToValue(filters, object) as Filter[]
    }, [action.settings?.reference_filters, object])

    const handleChangeSettings = (patch: Record<string, unknown>) => {
        onChange({
            settings: {
                ...action.settings,
                ...patch,
            },
        })
    }

    const handleChangeTestFilters = (newFilters: Filter[]) => {
        handleChangeSettings({
            test_filters: formatFilters(newFilters) as CompareRecordsActionFilter[],
        })
    }

    const handleChangeReferenceFilters = (newFilters: Filter[]) => {
        handleChangeSettings({
            reference_filters: formatFilters(newFilters) as CompareRecordsActionFilter[],
        })
    }

    const handleFieldsChange = (selectedFields: string[]) => {
        handleChangeSettings({
            compare_fields: selectedFields,
        })
    }

    const handleIdentifierChange = (value: string) => {
        handleChangeSettings({
            id_field: value,
        })
    }

    return (
        <>
            <ConfigurationLabel>Table</ConfigurationLabel>
            <ObjectPicker
                value={objectId}
                onChange={(item: string) => {
                    handleChangeSettings({
                        object_id: item,
                        stack_id: selectedStack?._sid,
                        // Clear field-specific settings when object changes
                        compare_fields: [],
                        id_field: '_sid',
                    })
                }}
                filter={(o: ObjectDto) => !o.connection_options.read_only}
                isClearable={false}
            />
            {object && (
                <>
                    <ConfigurationLabel>Record Identifier Field</ConfigurationLabel>
                    <Select
                        placeholder="Select identifier field"
                        value={identifierField || '_sid'}
                        onChange={handleIdentifierChange}
                    >
                        <SelectOption value="_sid" label="Record ID" />
                        {fields.map((field) => (
                            <SelectOption
                                key={field.api_name}
                                value={field.api_name}
                                label={field.label}
                            />
                        ))}
                    </Select>
                    <ConfigurationLabel>Fields to Compare</ConfigurationLabel>
                    <Select
                        placeholder="Select fields to compare"
                        value={compareFields}
                        onChange={handleFieldsChange}
                        multiSelect
                    >
                        {fields.map((field) => (
                            <SelectOption
                                key={field.api_name}
                                value={field.api_name}
                                label={field.label}
                            />
                        ))}
                    </Select>
                    <Divider mt="m" />
                    <ConfigurationLabel>Test Set Filters</ConfigurationLabel>
                    <Filters
                        object={object}
                        fields={fields}
                        value={testFiltersValue}
                        onChange={handleChangeTestFilters}
                        includeIncompleteFilters={false}
                        showRelativeDateFilters={true}
                        hideCurrentUserOption={true}
                        disableValidation
                        customButtonRender={(props) => (
                            <Box>
                                <Button
                                    {...props}
                                    type="button"
                                    variant="secondary"
                                    size="s"
                                    startIcon={{ name: 'Plus' }}
                                >
                                    Add filter
                                </Button>
                            </Box>
                        )}
                        // @ts-expect-error
                        FilterValueComponent={FilterValueExpression}
                    />
                    <Divider mt="m" />
                    <ConfigurationLabel>Reference Set Filters</ConfigurationLabel>
                    <Filters
                        object={object}
                        fields={fields}
                        value={referenceFiltersValue}
                        onChange={handleChangeReferenceFilters}
                        includeIncompleteFilters={false}
                        showRelativeDateFilters={true}
                        hideCurrentUserOption={true}
                        disableValidation
                        customButtonRender={(props) => (
                            <Box>
                                <Button
                                    {...props}
                                    type="button"
                                    variant="secondary"
                                    size="s"
                                    startIcon={{ name: 'Plus' }}
                                >
                                    Add filter
                                </Button>
                            </Box>
                        )}
                        // @ts-expect-error
                        FilterValueComponent={FilterValueExpression}
                    />

                    <Divider mt="m" />
                </>
            )}
        </>
    )
}
